/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createLogPrefix, Deferred, logDebug, type LogTarget, logWarn } from '@amazon/vinyl-util'
import type { AuthTokenProvider } from '@amzn/vinyl-amzn'
import { type Auth, AuthStatus } from '@amzn/bf-playback'
import { logStatusTransition } from '@/auth/authLog'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

declare global {
    interface Window {
        setAuthToken?: (authToken: string) => void
        setAuthFailed?: (reason: string) => void
    }
}

export class MapAuth implements Auth, LogTarget {
    logPrefix = createLogPrefix('MapAuth')

    private _authToken = ''
    private authTokenSet = new Deferred<void>()
    private _status = data(AuthStatus.PENDING)
    private _failure = data<string | null>(null)

    constructor() {
        window.setAuthToken = (authToken: string) => {
            // Presence only — never log the token value.
            logDebug(this, 'setAuthToken')
            if (!authToken) {
                // An empty token authenticates nothing. Announcing SIGNED_IN on
                // it would make the app take signed-in paths while every token
                // vend fails; staying PENDING keeps the MAP waiting surface up.
                logWarn(this, 'ignoring empty authToken')
                return
            }
            this._authToken = authToken
            this._failure.value = null
            this.authTokenSet.resolve()
            // Synchronous with the token so `isSignedIn()` and `status` can never
            // disagree; `logStatusTransition` ignores a repeat on rotation.
            logStatusTransition(this, this._status, AuthStatus.SIGNED_IN)
        }
        // The shell's word that MAP vended no token (e.g. the device isn't registered). The status
        // stays PENDING, as SIGNED_OUT would offer a sign-in the device can't do; the waiting
        // surface shows the failure instead. A token pushed later still signs in.
        window.setAuthFailed = (reason: string) => {
            if (this.isSignedIn()) return
            logWarn(this, `setAuthFailed: ${reason}`)
            this._failure.value = reason || 'unknown'
        }
    }

    /** Why the shell got no token from MAP, or null while none has failed. */
    get failure(): ObservableValue<string | null> {
        return this._failure
    }

    get status(): ObservableValue<AuthStatus> {
        return this._status
    }

    isSignedIn(): boolean {
        return this._authToken !== ''
    }

    usesBearer(): boolean {
        return true
    }

    get authTokenProvider(): AuthTokenProvider {
        return async (): Promise<string> => {
            await this.authTokenSet
            // Read on every vend rather than off the latch: the native shell
            // rotates an expiring token through `setAuthToken`.
            return this._authToken
        }
    }

    /**
     * For Map auth, this does nothing.
     */
    signIn() {
        logDebug(this, 'signIn (no-op)')
    }

    /**
     * For Map auth, this does nothing.
     */
    signOut(): Promise<void> {
        logDebug(this, 'signOut (no-op)')
        return Promise.resolve()
    }
}
